import {
  ArrowRight,
  BadgeCheck,
  FileText,
  ShieldCheck,
  TimerReset,
} from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { AuctionImage } from "./auction-image.tsx";
import { formatMoney } from "./format-money.ts";
import type { LockerverseStyle, LockerverseTheme } from "./theme.ts";

type AuctionPurchaseDetails = {
  purchaseId: string;
  unitAmountCents: number;
  quantity: number;
  subtotalCents: number;
  serviceFeeCents: number;
  totalCents: number;
};

export type LockerverseAuctionOutcome =
  | { kind: "bid"; amountCents?: number }
  | (AuctionPurchaseDetails & {
      kind: "purchase";
      /** Use only after the server confirms payment. Receipt amounts alone are not proof. */
      status: "paid";
    })
  | (AuctionPurchaseDetails & { kind: "receipt" });

export type LockerverseAuctionResultAction = {
  label: string;
  href?: string;
  onClick?: () => void;
};

export type LockerverseAuctionResultProps = {
  outcome: LockerverseAuctionOutcome;
  itemName: string;
  imageUrl?: string | undefined;
  auctionName?: string | undefined;
  communityName?: string | undefined;
  closesAtLabel?: string | undefined;
  primaryAction?: LockerverseAuctionResultAction | undefined;
  secondaryAction?: LockerverseAuctionResultAction | undefined;
  className?: string | undefined;
  theme?: LockerverseTheme | undefined;
  style?: LockerverseStyle | undefined;
  /** Focus the result after an inline checkout completes. Defaults to false. */
  focusHeading?: boolean | undefined;
};

function ResultAction({
  action,
  secondary = false,
}: {
  action: LockerverseAuctionResultAction;
  secondary?: boolean;
}) {
  const className = `lockerverse-auction-result__action${secondary ? " lockerverse-auction-result__action--secondary" : ""}`;
  const content = (
    <>
      {action.label}
      {secondary ? null : <ArrowRight aria-hidden="true" size={18} />}
    </>
  );
  return action.href ? (
    <a className={className} href={action.href} onClick={action.onClick}>
      {content}
    </a>
  ) : (
    <button className={className} onClick={action.onClick} type="button">
      {content}
    </button>
  );
}

const outcomeCopy = {
  bid: {
    assurance:
      "A bid is not a purchase. Payment is collected only if you win at settlement.",
    assuranceTitle: "Bid authorization saved",
    badge: "Bid submitted",
    description:
      "Your bid and payment method are recorded. You have not been charged for this bid.",
    next: "Other bidders can still place bids until the item closes.",
    nextTitle: "Follow the auction",
    receiptLabel: "Bid summary",
    steps: [
      "Your bid is recorded. This does not confirm that you have won.",
      "Your saved payment method can be charged if you win at settlement.",
      "Watch your email for bid updates and return to the item to check its status.",
    ],
    title: "Bid received",
  },
  purchase: {
    assurance: "This receipt shows the amount paid for your purchase.",
    assuranceTitle: "Payment processed",
    badge: "Purchase confirmed",
    description: "Your payment is confirmed and your purchase is recorded.",
    next: "The community handles delivery or collection for this item.",
    nextTitle: "Next steps",
    receiptLabel: "Purchase receipt",
    steps: [
      "Your payment is complete and your purchase is recorded.",
      "The community can review your buyer and delivery details.",
      "Keep your receipt number. Contact the community if you need help with your order.",
    ],
    title: "Purchase complete",
  },
  receipt: {
    assurance:
      "The amounts below are the recorded purchase amounts, not proof of payment.",
    assuranceTitle: "Recorded purchase",
    badge: "Purchase details",
    description: "Payment status is not confirmed on this page.",
    next: "Contact the community if you need to confirm your payment or order status.",
    nextTitle: "Next steps",
    receiptLabel: "Purchase receipt",
    steps: [
      "These are the recorded purchase details. They do not confirm payment.",
      "Check your purchase confirmation for the payment result.",
      "Contact the community with your receipt number if you need help.",
    ],
    title: "Purchase details",
  },
};

function getResultDetails({
  outcome,
  auctionName,
  communityName,
  closesAtLabel,
}: LockerverseAuctionResultProps) {
  const details: [string, string][] = [];
  if (outcome.kind === "bid") {
    if (outcome.amountCents !== undefined) {
      details.push(["Bid amount", formatMoney(outcome.amountCents)]);
    }
  } else {
    details.push(
      ["Unit price", formatMoney(outcome.unitAmountCents)],
      ["Quantity", String(outcome.quantity)],
      ["Subtotal", formatMoney(outcome.subtotalCents)]
    );
    if (outcome.serviceFeeCents > 0) {
      details.push(["Service fee", formatMoney(outcome.serviceFeeCents)]);
    }
    details.push(
      [
        outcome.kind === "purchase" ? "Total paid" : "Total",
        formatMoney(outcome.totalCents),
      ],
      ["Receipt", outcome.purchaseId]
    );
  }
  if (auctionName) {
    details.push(["Auction", auctionName]);
  }
  if (communityName) {
    details.push(["Community", communityName]);
  }
  if (closesAtLabel) {
    details.push(["Closes", closesAtLabel]);
  }
  return details;
}

/** Shared result view. Hosts must provide a verified outcome or a neutral receipt. */
export function LockerverseAuctionResult(props: LockerverseAuctionResultProps) {
  const {
    outcome,
    itemName,
    imageUrl,
    communityName,
    primaryAction,
    secondaryAction,
    className,
    theme = "dark",
    style,
  } = props;
  const titleId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (props.focusHeading) {
      headingRef.current?.focus();
    }
  }, [props.focusHeading]);
  const copy = outcomeCopy[outcome.kind];
  const StatusIcon = outcome.kind === "receipt" ? FileText : BadgeCheck;
  const AssuranceIcon = outcome.kind === "receipt" ? FileText : ShieldCheck;
  const details = getResultDetails(props);
  return (
    <section
      aria-labelledby={titleId}
      className={["lockerverse-auctions lockerverse-auction-result", className]
        .filter(Boolean)
        .join(" ")}
      data-lockerverse-theme={theme}
      style={style}
    >
      <div className="lockerverse-auction-result__layout">
        <div className="lockerverse-auction-result__summary">
          <span className="lockerverse-auction-result__badge">
            <StatusIcon aria-hidden="true" size={18} />
            {copy.badge}
          </span>
          {communityName ? (
            <p className="lockerverse-auction-result__community">
              {communityName}
            </p>
          ) : null}
          <h2
            className="lockerverse-auction-result__title"
            id={titleId}
            ref={headingRef}
            tabIndex={props.focusHeading ? -1 : undefined}
          >
            {copy.title} for {itemName}.
          </h2>
          <p className="lockerverse-auction-result__description">
            {copy.description}
          </p>
          <div className="lockerverse-auction-result__assurance">
            <AssuranceIcon aria-hidden="true" size={22} />
            <div>
              <strong>{copy.assuranceTitle}</strong>
              <p>{copy.assurance}</p>
            </div>
          </div>
          <div className="lockerverse-auction-result__assurance">
            <TimerReset aria-hidden="true" size={22} />
            <div>
              <strong>{copy.nextTitle}</strong>
              <p>{copy.next}</p>
            </div>
          </div>
          {primaryAction || secondaryAction ? (
            <div className="lockerverse-auction-result__actions">
              {primaryAction ? <ResultAction action={primaryAction} /> : null}
              {secondaryAction ? (
                <ResultAction action={secondaryAction} secondary />
              ) : null}
            </div>
          ) : null}
        </div>
        <aside
          aria-label={copy.receiptLabel}
          className="lockerverse-auction-result__receipt"
        >
          {imageUrl ? (
            <AuctionImage
              alt={itemName}
              className="lockerverse-auction-result__image"
              height={300}
              maxWidth={960}
              sizes="100vw"
              src={imageUrl}
              width={420}
            />
          ) : null}
          <div className="lockerverse-auction-result__receipt-body">
            <p className="lockerverse-auction-result__eyebrow">
              {copy.receiptLabel}
            </p>
            <h3 className="lockerverse-auction-result__receipt-title">
              {itemName}
            </h3>
            <dl className="lockerverse-auction-result__details">
              {details.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            <h4>What happens next</h4>
            {/* biome-ignore lint/a11y/noRedundantRoles: Keep list semantics in Safari when visual markers replace list-style. */}
            <ol className="lockerverse-auction-result__steps" role="list">
              {copy.steps.map((step, index) => (
                <li key={step}>
                  <span
                    aria-hidden="true"
                    className="lockerverse-auction-result__step-number"
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </div>
    </section>
  );
}
